import React, {useEffect, useMemo, useRef, useState} from 'react';
import classes from '../ChatBuilder.module.css';
import * as timeago from 'timeago.js';
import { defaultStyles, FileIcon } from 'react-file-icon'
import {database, storage} from "../../../firebase";
import BootstrapModal from "react-bootstrap/Modal";
import styles from "../../../components/Image/image.module.css";
import {connect} from "react-redux";
import { Button, Input, message, Modal, notification, Popconfirm, Select, Tabs } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { ENVIRONMENT } from '../../../environment';
import CallRecordingPlayer from '../../../components/CallRecordingPlayer/CallRecordingPlayer';
import PatientTypingBubble from '../../../components/TypingBubble/PatientTypingBubble';
import { useTypingListener } from '../../../hooks/useTypingIndicator';
import {meetingAiOutcome} from '../../../utils/meetingAiOutcome';

const soapNoteAiBaseUrl = ENVIRONMENT === 'production'
    ? 'https://northamerica-northeast1-yourdoctorsonline-prod.cloudfunctions.net'
    : 'https://northamerica-northeast1-yourdoctorsonline-staging.cloudfunctions.net';

const SOAP_NOTE_AI_ENDPOINT = `${soapNoteAiBaseUrl}/soapNotesAiPostMeeting`;

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const parseTranscript = (transcriptText) => {
    if (!transcriptText || typeof transcriptText !== 'string') return [];

    const lines = transcriptText.split(/(\d{2}:\d{2}:\d{2})/).filter(Boolean);
    const parsed = [];

    for (let i = 0; i < lines.length; i += 2) {
      if (lines[i].match(/\d{2}:\d{2}:\d{2}/) && lines[i + 1]) {
        const timestamp = lines[i];
        const content = lines[i + 1].trim();

        const speakerMatch = content.match(/^\[(.+?)\]:(.*)/);;
        if (speakerMatch) {
          parsed.push({
            timestamp,
            speaker: speakerMatch[1],
            text: speakerMatch[2].trim()
          });
        }
      }
    }

    return parsed;
};

const TranscriptModal = props => {
    const [activeTab, setActiveTab] = useState('transcript');
    const [editableEntries, setEditableEntries] = useState(() => parseTranscript(props.transcript));
    const [editableSummary, setEditableSummary] = useState(props.summary || '');
    const [participantAction, setParticipantAction] = useState('delete');
    const [saving, setSaving] = useState(false);
    const [sendingSummary, setSendingSummary] = useState(false);

    const parsedTranscript = useMemo(() => parseTranscript(props.transcript), [props.transcript]);
    const isAdmin = props.isTranscriptAdmin !== undefined
        ? Boolean(props.isTranscriptAdmin)
        : Boolean(props.canEditTranscript);
    const entriesToRender = editableEntries;

    useEffect(() => {
        setEditableEntries(parsedTranscript);
    }, [parsedTranscript]);

    useEffect(() => {
        setEditableSummary(props.summary || '');
    }, [props.summary]);

    const handleEntryChange = (index, field, value) => {
        setEditableEntries(prev => {
            const updated = [...prev];
            updated[index] = {...updated[index], [field]: value};
            return updated;
        });
    };

    const handleDeleteEntry = (index) => {
        const updated = editableEntries.filter((_, i) => i !== index);
        if (updated.length === editableEntries.length) {
            message.info('No entry removed');
            return;
        }
        setEditableEntries(updated);
        message.success('Entry deleted');
    };

    const handleSwapSpeakers = () => {
        
        let doctorCount = 0;
        let patientCount = 0;
        const updated = editableEntries.map(entry => {
            if (entry.speaker === 'Doctor') {
                doctorCount++;
                return {...entry, speaker: 'Patient'};
            }
            if (entry.speaker === 'Patient') {
                patientCount++;
                return {...entry, speaker: 'Doctor'};
            }
            return entry;
        });
        if (doctorCount === 0 && patientCount === 0) {
            message.info('No doctor/patient entries to swap');
            return;
        }
        setEditableEntries(updated);
        message.success(`Swapped speakers: ${doctorCount} Doctor → Patient, ${patientCount} Patient → Doctor`);
    };

    const handleParticipantAction = () => {
    
        const hasParticipants = editableEntries.some(entry => entry.speaker === 'Participant');
        if (!hasParticipants) {
            message.info('No Participant entries found');
            return;
        }

        if (participantAction === 'delete') {
            const filtered = editableEntries.filter(entry => entry.speaker !== 'Participant');
            const deletedCount = editableEntries.length - filtered.length;
            setEditableEntries(filtered);
            if (deletedCount > 0) {
                message.success(`Deleted ${deletedCount} Participant entr${deletedCount === 1 ? 'y' : 'ies'}`);
            } else {
                message.info('No Participant entries removed');
            }
            return;
        }

        const targetSpeaker = participantAction === 'doctor' ? 'Doctor' : 'Patient';
        let changedCount = 0;
        const updated = editableEntries.map(entry => {
            if (entry.speaker === 'Participant') {
                changedCount++;
                return {...entry, speaker: targetSpeaker};
            }
            return entry;
        });
        setEditableEntries(updated);
        if (changedCount > 0) {
            message.success(`Changed ${changedCount} Participant entr${changedCount === 1 ? 'y' : 'ies'} to ${targetSpeaker}`);
        } else {
            message.info('No Participant entries updated');
        }
    };

    const buildTranscriptPayload = () =>
        editableEntries
            .map(entry => `${entry.timestamp} [${entry.speaker}]: ${entry.text}`)
            .join('\n');

    const handleSaveTranscript = async () => {
        if (!props.conversationId || !props.messageKey) {
            message.error('Missing conversation reference for this transcript.');
            return;
        }

        setSaving(true);
        try {
            const reconstructed = buildTranscriptPayload();
            if(props.messageKey){
            await database
                .ref(`/conversations/${props.conversationId}/${props.messageKey}`)
                .update({
                    transcription: reconstructed,
                    transcriptSummary: editableSummary || ''
                });
            message.success('Transcript saved successfully');
            }
            props.onClose?.();
        } catch (error) {
            console.error('Error saving transcript:', error);
            message.error('Failed to save transcript');
        } finally {
            setSaving(false);
        }
    };

    const resolvePatientId = () => (
        props.patientId ||
        props.selectedPatient?.patientID ||
        props.selectedPatient?.patientId ||
        props.selectedPatient?.patientFirebaseID ||
        props.selectedPatient?.user_id ||
        null
    );

    const handleSendSummary = async () => {
        if (!editableSummary.trim()) {
            message.warning('Summary is empty');
            return;
        }
        if (!props.conversationId) {
            message.error('Missing conversation reference');
            return;
        }
        const patientIdentifier = resolvePatientId();
        if (!patientIdentifier) {
            message.error('Missing patient identifier');
            return;
        }

        const doctorInfo = Array.isArray(props.doctorDetail) ? props.doctorDetail[0] : props.doctorDetail;
        if (!doctorInfo || !doctorInfo.uid) {
            message.error('Missing doctor information');
            return;
        }

        setSendingSummary(true);
        try {
            const conversationRef = database.ref(`/conversations/${props.conversationId}`);
            const newMessageRef = conversationRef.push();
            const now = Date.now();
            await newMessageRef.set({
                type: 'text',
                content: editableSummary.trim(),
                fromID: doctorInfo.uid,
                toID: patientIdentifier,
                isRead: false,
                doctorName: doctorInfo.doctorName || 'Doctor',
                doctorImgURL: doctorInfo.profileImageURL || '',
                timestamp: now
            });

            if (props.messageKey) {
                await database
                    .ref(`/conversations/${props.conversationId}/${props.messageKey}`)
                    .update({ transcriptSummary: editableSummary.trim() });
            }
            message.success('Summary sent to chat successfully');
            props.onClose?.();
        } catch (error) {
            console.error('Error sending summary:', error);
            message.error('Failed to send summary to chat');
        } finally {
            setSendingSummary(false);
        }
    };

    const hasParticipants = editableEntries.some(entry => entry.speaker === 'Participant');
    const showParticipantTools = isAdmin && hasParticipants;

    const footerButtons = [
        <Button key="cancel" onClick={props.onClose}>
            Close
        </Button>
    ];

    if (activeTab === 'summary') {
        footerButtons.push(
            <Button
                key="send"
                type="primary"
                loading={sendingSummary}
                onClick={handleSendSummary}
                disabled={!editableSummary.trim()}
            >
                📋 Send Summary to Chat
            </Button>
        );
    } else {
        footerButtons.push(
            <Button
                key="save"
                type="primary"
                loading={saving}
                onClick={handleSaveTranscript}
                disabled={editableEntries.length === 0}
                title={isAdmin ? '' : 'Only admins can save transcript changes'}
            >
                Save Changes
            </Button>
        );
    }

    return (
        <Modal
            title="Meeting Transcript"
            open
            onCancel={props.onClose}
            width={1000}
            footer={footerButtons}
            destroyOnClose={false}
            maskClosable={false}
            bodyStyle={{ maxHeight: '75vh', overflowY: 'auto', paddingRight: 12 }}
            style={{ top: 24 }}
        >
            <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
                <TabPane tab="View Transcript" key="transcript">
                    <div className={classes.transcript_view}>
                        {entriesToRender.length > 0 && (
                            <div
                                className={classes.transcript_admin_toolbar || ''}
                                style={{
                                    marginBottom: showParticipantTools ? '16px' : '8px',
                                    borderBottom: showParticipantTools ? '1px solid #f0f0f0' : 'none',
                                    paddingBottom: showParticipantTools ? '12px' : 0
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 600 }}>Quick Actions:</span>
                                    <Button
                                        size="small"
                                        onClick={handleSwapSpeakers}
                                        disabled={editableEntries.length === 0}
                                        title={!isAdmin ? 'Only admins can swap speakers' : ''}
                                    >
                                        Swap Doctor ↔ Patient
                                    </Button>
                                </div>
                                {showParticipantTools && (
                            <div
                                style={{
                                    marginTop: '16px',
                                    border: '1px solid #ff4d4f',
                                    borderRadius: '4px',
                                    padding: '12px',
                                    backgroundColor: '#fff1f0'
                                }}
                            >
                                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#a8071a' }}>
                                    ⚠️ Handle Participants:
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Select
                                        value={participantAction}
                                        onChange={setParticipantAction}
                                        size="small"
                                        style={{ width: 180 }}
                                    >
                                        <Option value="doctor">Convert to Doctor</Option>
                                        <Option value="patient">Convert to Patient</Option>
                                        <Option value="delete">Delete Entries</Option>
                                    </Select>
                                    <Button
                                        size="small"
                                        type="primary"
                                        onClick={handleParticipantAction}
                                        danger={participantAction === 'delete'}
                                        disabled={saving}
                                    >
                                        ⚠️ {participantAction === 'delete' ? 'Delete All Participants' : 'Convert All Participants'}
                                    </Button>
                                </div>
                            </div>
                        )}
                            </div>
                        )}

                        {props.conversationId && props.messageKey && (
                            <CallRecordingPlayer
                                sessionID={props.conversationId}
                                messageKey={props.messageKey}
                                label="Call Recording"
                            />
                        )}

                        <div className={classes.transcript_entries_container || ''} style={{ marginTop: '16px' }}>
                            {entriesToRender.length > 0 ? (
                                entriesToRender.map((item, idx) => {
                                    const isParticipantEntry = item.speaker === 'Participant';
                                    return (
                                        <div
                                            key={`${item.timestamp}-${idx}`}
                                            className={classes.transcript_item || ''}
                                            style={{
                                                border: isParticipantEntry ? '2px solid #ff4d4f' : '1px solid #e0e0e0',
                                                borderRadius: '6px',
                                                padding: '12px',
                                                marginBottom: '12px',
                                                backgroundColor: isParticipantEntry ? '#fff1f0' : '#fff'
                                            }}
                                        >
                                            <div
                                                className={classes.transcript_item_header || ''}
                                                style={{
                                                    display: 'flex',
                                                    gap: '8px',
                                                    alignItems: 'center',
                                                    marginBottom: '8px'
                                                }}
                                            >
                                                <span
                                                    className={classes.transcript_timestamp}
                                                    style={isParticipantEntry ? { color: '#a8071a', fontWeight: 600 } : {}}
                                                >
                                                    {item.timestamp}
                                                </span>
                                                {/* {isAdmin ? ( */}
                                                    <Select
                                                        value={item.speaker}
                                                        onChange={(value) => handleEntryChange(idx, 'speaker', value)}
                                                        size="small"
                                                        className={classes.transcript_input || ''}
                                                        style={isParticipantEntry ? { minWidth: 140, borderColor: '#ff4d4f' } : { minWidth: 140 }}
                                                    >
                                                        <Option value="Doctor">Doctor</Option>
                                                        <Option value="Patient">Patient</Option>
                                                        {/* <Option value="Participant">
                                                            ⚠️ Participant
                                                        </Option> */}
                                                    </Select>
                                                {/* ) : (
                                                    <span className={classes.transcript_speaker}>[{item.speaker}]</span>
                                                )} */}
                                                {isAdmin && (
                                                    <Popconfirm
                                                        title="Delete this entry?"
                                                        description="Are you sure you want to delete this transcript entry?"
                                                        onConfirm={() => handleDeleteEntry(idx)}
                                                        okText="Yes"
                                                        cancelText="No"
                                                        disabled={saving}
                                                    >
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            size="small"
                                                            disabled={saving}
                                                        />
                                                    </Popconfirm>
                                                )}
                                            </div>
                                            {isAdmin ? (
                                                <TextArea
                                                    value={item.text}
                                                    onChange={(e) => handleEntryChange(idx, 'text', e.target.value)}
                                                    placeholder="Enter transcript text..."
                                                    autoSize={{ minRows: 1, maxRows: 8 }}
                                                    className={classes.transcript_textarea || ''}
                                                    style={isParticipantEntry ? { borderColor: '#ff4d4f' } : {}}
                                                />
                                            ) : (
                                                <p className={classes.transcript_text}>{item.text}</p>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <p className={classes.transcript_empty_state || ''}>
                                    No transcript entries found. Please wait for the AI to finish processing.
                                </p>
                            )}
                        </div>
                    </div>
                </TabPane>
                <TabPane tab="Summary" key="summary">
                    <div className={classes.summary_view}>
                        <TextArea
                            value={editableSummary}
                            onChange={(e) => setEditableSummary(e.target.value)}
                            placeholder="Meeting summary will appear here once available."
                            rows={12}
                            className={classes.transcript_textarea || ''}
                            style={{
                                fontSize: '14px',
                                lineHeight: '1.8',
                                fontFamily: 'monospace'
                            }}
                        />
                    </div>
                </TabPane>
            </Tabs>
        </Modal>
    );
};

const MessageList = (props) => {
    const [showModal, setShowModal] = useState(false)
    const [showTranscriptModal, setShowTranscriptModal] = useState(false)
    const [isProcessingAI, setIsProcessingAI] = useState(false)
    const patientDisplayName = props.selectedPatient?.patientName || props.patientName || 'Patient';
    const patientInitial = (patientDisplayName.charAt(0) || 'P').toUpperCase();
    const matchpattern = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/gm
    const content = props.text
    const {type} = props
    let status = props.isRead ? 'Read' : 'Delivered';
    let time = timeago.format(props.timestamp);
    const [contentType, setContentType] = useState(null)
    let bubbleData = content
    const onHideModal = () => {
        // console.log("onHideModal clicked")
        setShowModal(false)
    }
    // console.log("MessageList triggered")
    useEffect(()=>{
        if(type === 'application' || type === 'video' || type === 'audio'){
            console.log("content ", content)
            try{
                storage.refFromURL(content)
                    .getMetadata()
                    .then((metadata) => {
                        console.log("storage metadata ", metadata)
                        const cType = metadata?.contentType
                        const cTypeSplit = cType.split('/')
                        setContentType(cTypeSplit[cTypeSplit.length - 1])
                        // Metadata now contains the metadata?.contentType for 'images/forest.jpg'
                    })
                    .catch((error) => {
                        console.log("storage error ", error)
                        // Uh-oh, an error occurred!
                    });
            }catch(error){
                const cTypeSplit = content.split('.')
                if(cTypeSplit.length>0){
                    setContentType(cTypeSplit[cTypeSplit.length-1])
                }

            }

        }
    },[])
    const renderModal = () => <BootstrapModal
        show={showModal} centered
        onHide={()=>{setShowModal(false)}}
        toggle={onHideModal}> {type === 'video' ?
        <video width='320' height='240' controls
               className='align-self-center  h-100'>
            <source src={content} type='video/mp4' />
            Your browser does not support the video tag.
        </video> : <img src={content} alt='full image' />
    }</BootstrapModal>;

    const handleProcessMeetingAI = async () => {
        try {
            setIsProcessingAI(true);
            const payload = {
                chatID: props.conversationId,
                messageID: props.messageKey,
                chatType: 'family_doctor',
                sessionID: null
            };
            console.log('🤖 Sending request to AI processing...', payload);
            const response = await fetch(SOAP_NOTE_AI_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Failed to process AI request.');
                throw new Error(errorText || 'Failed to process AI request.');
            }
            // Only the "no transcript" answer is a warning; any other status false
            // (e.g. a failed save) is an error (utils/meetingAiOutcome).
            const result = await response.json().catch(() => null);
            const outcome = meetingAiOutcome(true, result);
            if (outcome === 'error') {
                throw new Error((result && result.message) || 'Failed to process AI request.');
            }
            if (outcome === 'no_transcript') {
                notification.warning({
                    message: 'No transcript for this call',
                    description: result.message || 'This call has no transcript, so there is nothing to summarise.',
                    duration: 5
                });
                return;
            }
            console.log('✓ AI processing request sent successfully');
            notification.success({
                message: 'AI Processing Started',
                description: 'The meeting is being processed. Transcript and summary will appear shortly.',
                duration: 5
            });
        } catch (error) {
            console.error('❌ AI processing error:', error);
            notification.error({
                message: 'AI Processing Failed,Transcript Not Exist',
                description: 'Failed to process meeting from AI.',
                duration: 5
            });
        } finally {
            setIsProcessingAI(false);
        }
    };

    const renderTranscriptButton = () => {
        if (props.transcription || props.transcriptSummary) {
            return (
                <button
                    className={classes.view_transcript_btn}
                    onClick={() => setShowTranscriptModal(true)}
                >
                    📄 View Transcript
                </button>
            );
        }
        if ((type === 'videoCall' || type === 'audioCall' || type==="link") && !props.transcription && !props.transcriptSummary) {
            return (
                <button
                    className={classes.view_transcript_btn}
                    onClick={handleProcessMeetingAI}
                    disabled={isProcessingAI}
                >
                    {isProcessingAI ? '⏳ Processing...' : '🤖 Process Meeting from AI'}
                </button>
            );
        }
        return null;
    };

    // Check if this is a system message - ONLY check the isSystemMessage prop
    const isSystemMessage = props.isSystemMessage === true;

    // If it's a system message, render with special styling
    if (isSystemMessage) {
        return (
            <li className={classes.system_message_li}>
                <div className={classes.system_message_wrapper}>
                    <div className={classes.system_message_divider}>
                        <span className={classes.system_message_line}></span>
                        <span className={classes.system_message_timestamp}>{time}</span>
                        <span className={classes.system_message_line}></span>
                    </div>
                    <div className={classes.system_message_content}>
                        <span className={classes.system_message_text}>{content}</span>
                        {props.transferredByName && (
                            <span
                                className={classes.system_message_meta}
                                title={props.transferredByID ? `uid: ${props.transferredByID}` : undefined}
                            >
                                Transferred by {props.transferredByName}
                            </span>
                        )}
                    </div>
                </div>
            </li>
        );
    }

    // For all other messages, use the existing rendering logic
    switch (type) {
        case 'image':
            bubbleData =
                <div>
                    <img src={content} className={classes.imageWidth} alt='image'
                     onClick={() => setShowModal(true)} />
                    <div className={styles.copyLink} onClick={()=>navigator.clipboard.writeText(content || '')}>Copy Link</div>
                </div>
            break
        case 'videoCall':
        case 'audioCall':
            bubbleData = (
                <div className={classes.link_message_container}>
                    <div>{content}</div>
                    {renderTranscriptButton()}
                </div>
            );
            break
        case 'video':
            bubbleData = <div className={classes.iconBubble}>
                    <div onClick={() => setShowModal(true)} className={classes.fileIcon}>
                        {/* <VideoImageThumbnail
                            videoUrl={content}
                            thumbnailHandler={(thumbnail) => console.log("thumbnaill ", thumbnail)}
                            width={155}
                            height={112}
                            alt="video"
                            cors={true}
                            snapshotAtTime={1}

                        /> */}
                        <FileIcon
                            extension={contentType} {...defaultStyles[contentType]} />
                    </div>
                <div className={styles.copyLink} onClick={()=>navigator.clipboard.writeText(content || '')}>Copy Link</div>
            </div>
            break
        case 'link':
            bubbleData = <div className={classes.link_message_container}>
                <div className={classes.link_text}>
                    {content}
                </div>
                {renderTranscriptButton()}
            </div>
            break
        /*case 'application':
        case 'audio':
            bubbleData = <div className={classes.fileIcon}>
                <FileIcon
                    extension={contentType} {...defaultStyles[contentType]} />
            </div>
            break*/
        default:
            if (matchpattern.test(content)) {
                bubbleData = <a href={content} target='#'
                    /*className={props.direction === 'left' ? classes.textLeft : classes.textRight }*/>{content}</a>
            }
            break
    }
    if (props.direction === 'left') {
        return (
            <>
            <li className={classes.message_li}>
                <div className={classes.message_wrapper}>
                    <span className={classes.author}>{patientDisplayName}</span>
                    <div className={classes.user_symbol}>
                        <div className={classes.user_img}>
                            <span className={classes.user_symbol_content}>{patientInitial}</span>
                        </div>
                    </div>
                    <div className={classes.message_container}>
                        <div className={classes.message_text}>

                            {(props.type === "image" || props.type === "video" || props.type === "application" || props.type === "link" || props.type === "audioCall" || props.type === "videoCall")?
                                bubbleData :  props.type === "audio" ? (
                                    <div className={classes.audioBubbleright}>
                                    <audio controls src={props.text}> </audio>
                                    </div>
                                  ) : props.type === "text" && props.type === "videoCall" && props.type === "audioCall"? <span>{props.text}</span> : <span>{props.text}</span>
                            /*<a href={props.text} target="_blank" rel="noopener noreferrer" alt="">
                                <p>{props.text}</p>
                            </a>*/
                            }
                            
                        </div>
                    </div>
                    <div className={classes.message_time_left}>
                        <span className={classes.timestamp_span_left}>{time}</span>
                        <span className={classes.read_span_left}>{status}</span>
                    </div>
                </div>
            </li>
                {showModal && renderModal()}
                {showTranscriptModal && (
                    <TranscriptModal 
                        transcript={props.transcription}
                        summary={props.transcriptSummary}
                        onClose={() => setShowTranscriptModal(false)}
                        canEditTranscript={props.canEditTranscript}
                        isTranscriptAdmin={props.isTranscriptAdmin}
                        conversationId={props.conversationId}
                        messageKey={props.messageKey}
                        doctorDetail={props.doctorDetail}
                        selectedPatient={props.selectedPatient}
                        patientId={props.patientId}
                    />
                )}
            </>
        )
    }
    else {
        return (
            <>

            <li className={classes.message_li_right}>
                <div className={classes.message_wrapper_right}>
                    <span className={classes.author_right}>{props.doctorName}</span>
                    <div className={classes.user_symbol_right}>
                        <div className={classes.user_symbol_content_right}>
                            <span>
                                <img
                                    src={props.doctorImage}
                                    className={classes.user_img_right}
                                    alt=""
                                />
                            </span>
                        </div>
                        <div className={classes.message_container}>
                            <div className={classes.message_text_right}>
                                {(props.type === "image" || props.type === "video" || props.type === "application" || props.type === "link" || props.type === "audioCall" || props.type === "videoCall")?
                                    bubbleData : props.type === "audio" ? (
                                        <div className={classes.audioBubbleright}>
                                        <audio controls src={props.text}> </audio>
                                        </div>
                                      ) : props.type === "text" && props.type === "videoCall" && props.type === "audioCall"? <span>{props.text}</span> : <span>{props.text}</span>
                                    /*<a href={props.text} target="_blank" rel="noopener noreferrer" alt="">
                                        <p>{props.text}</p>
                                    </a>*/
                                    
                                }
                            </div>
                        </div>
                        <div className={classes.message_time_right}>
                            <span className={classes.timestamp_span_right}>{time}</span>
                            <span className={classes.read_span_right}>{status}</span>
                        </div>
                    </div>
                </div>
            </li>
                {showModal && renderModal()}
                {showTranscriptModal && (
                    <TranscriptModal 
                        transcript={props.transcription}
                        summary={props.transcriptSummary}
                        onClose={() => setShowTranscriptModal(false)}
                        canEditTranscript={props.canEditTranscript}
                        isTranscriptAdmin={props.isTranscriptAdmin}
                        conversationId={props.conversationId}
                        messageKey={props.messageKey}
                        doctorDetail={props.doctorDetail}
                        selectedPatient={props.selectedPatient}
                        patientId={props.patientId}
                    />
                )}
            </>
        )
    }
}

const ChatList = (props) => {
    const messagesEndRef = useRef(null);
    //console.log("ChatList rendered")
    const [messages, setMessages] = useState([]);
    const doctorProfile = Array.isArray(props.doctorDetail) ? props.doctorDetail[0] : props.doctorDetail;
    const canEditTranscript = Boolean(props.admin || doctorProfile?.role === 'Admin' || doctorProfile?.isadmin || doctorProfile?.isAdmin);
    const isTranscriptAdmin = Boolean(props.admin);

    // Live view of what the patient is currently typing. Only shown on the open
    // conversation -- never when browsing chat history.
    const typingPatientUid =
        props.patientId ||
        props.selectedPatient?.patientID ||
        props.selectedPatient?.patientId ||
        props.selectedPatient?.patientFirebaseID ||
        props.selectedPatient?.user_id ||
        null;
    const patientTyping = useTypingListener(props.conversationId, typingPatientUid);
    const isViewingHistory = props.chatHistoryItemClick || props.chatHistoryItemClick === 0;
    const showPatientTyping = Boolean(patientTyping?.text) && !isViewingHistory;
    const typingPatientName = props.selectedPatient?.patientName || 'Patient';
    const typingPatientInitial = (typingPatientName.charAt(0) || 'P').toUpperCase();

    useEffect(()=>{
        //console.log("ChatList chatHistoryItemClick", props.chatHistoryItemClick)
        setMessages((props.chatHistoryItemClick || props.chatHistoryItemClick === 0 )?props.chatHistory[props.chatHistoryItemClick]?.messages|| []: props.messages)
    },[props.chatHistoryItemClick,props.messages, props.chatHistory])


   
    const scrollToBottom = () => {
        try{
            if(!props.loading_messages && messagesEndRef.current)
                setTimeout(()=>messagesEndRef.current.scrollIntoView({behavior: "smooth"}),10);
        }catch (e) {
            console.log("scrollToBottom e", e)
        }

    }


    // Scroll on new messages, and once when the typing bubble appears -- keyed on
    // the boolean so it does not re-scroll on every keystroke the patient makes.
    useEffect(scrollToBottom, [messages, showPatientTyping]);

    useEffect(()=>{
        console.log("messages updated", messages)
    }, [messages]);

    return (
        <div className={classes.chatcontent}>
            <div className={classes.chatcontainer}>
                {!props.loading_messages &&
                <ul className={classes.chat_ul}>
                    <li>
                        <ul className={classes.chat_main_ul}>
                            {messages.map(message => {
                                let chatDoctor = props.doctorsNameAndImages?.length !== 0 && props.doctorsNameAndImages?.find(doctor => doctor.uid === message.receiveMessage.fromID);
                                return <MessageList
                                    key={message.receiveMessage.timestamp}
                                    type={message.receiveMessage.type}
                                    isRead={message.receiveMessage.isRead}
                                    direction={message.receiveMessage.direction}
                                    text={message.receiveMessage.content}
                                    timestamp={message.receiveMessage.timestamp}
                                    doctorName={chatDoctor? chatDoctor.doctorName : message.receiveMessage.doctorName}
                                    doctorImage={chatDoctor? chatDoctor.doctorName : message.receiveMessage.doctorImage}
                                    patientName={props?.selectedPatient?.patientName}
                                    selectedPatient={props.selectedPatient}
                                    isSystemMessage={message.receiveMessage.isSystemMessage || false}
                                    transferredByName={message.receiveMessage.transferredByName || ''}
                                    transferredByID={message.receiveMessage.transferredByID || ''}
                                    transcription={message.receiveMessage.transcription}
                                    transcriptSummary={message.receiveMessage.transcriptSummary}
                                    conversationId={props.conversationId}
                                    patientId={props.patientId}
                                    messageKey={message.timestamp || message.receiveMessage.timestamp || ''}
                                    canEditTranscript={canEditTranscript}
                                    isTranscriptAdmin={isTranscriptAdmin}
                                    doctorDetail={doctorProfile}
                                />
                            })}

                            {showPatientTyping &&
                            <PatientTypingBubble
                                name={typingPatientName}
                                initial={typingPatientInitial}
                                text={patientTyping.text}
                            />
                            }
                        </ul>
                        <div ref={messagesEndRef}/>
                    </li>
                </ul>
                }
                {props.loading_messages &&
                <div className={classes.no_loading}>
                    <div className={classes.no_loading_view}>
                        <div className={classes.loading_loader}/>
                    </div>
                </div>
                }
            </div>

        </div>

    )
}
const mapStateToProps = ({ conversationReducer, chatReducer, doctorReducer }) => {
    const {
        doctorsNameAndImages,
        chatHistoryItemClick,
        selectedPatient,
        conversationId,
        patientId,
        admin,
    } = conversationReducer;

    const  {
        doctorDetail
    } = doctorReducer
    const {
        loading_messages,
        doctorName,
        doctorImage,
        messages,
        chatHistory
    } = chatReducer;
    return {
        loading_messages,
        doctorsNameAndImages,
        doctorName,
        doctorImage,
        messages,
        chatHistory,
        chatHistoryItemClick,
        selectedPatient,
        doctorDetail,
        conversationId,
        patientId,
        admin
    };
};
export default connect(mapStateToProps, {})(ChatList);
